import { priceLabel } from "../../lib/price-label";
import type { CSSProperties, ReactElement } from "react"

import { SillLogo } from "../brand/sill-logo"

export type PassportCardMode = "agent" | "own"

export interface PassportMoneyBand {
  low: number
  high: number
  period?: string
}

/**
 * Estimated buying power.
 *
 * `low` is OPTIONAL: SILL-30 removed the fabricated low (it was High × 0.85),
 * so passports written after it carry only a `high` and render one canonical
 * figure. Never substitute 0 for an absent low — that renders "$0–$2.7M".
 */
export interface PassportBuyingPower {
  low?: number
  high: number
  rate_percent?: number
  rate_as_of?: string
}

export interface PassportLine {
  kind: string
  state?: string
  band?: PassportMoneyBand | null
  provenance?: string
  method?: string
  verified_as_of?: string | null
  basis?: string
}

export interface PassportCardData {
  passport_id: string
  display_code?: string
  tier?: string
  variant?: string
  goal?: string
  timeline?: string
  target_area?: string
  price_bracket?: PassportMoneyBand | null
  price_kind?: "shopping_range" | "property_bracket" | "target_bracket"
  price_approximate?: boolean
  activity_bucket?: string
  buying_power?: PassportBuyingPower | null
  lines?: PassportLine[]
  offer_out?: boolean
}

/**
 * Which surface the card paints on.
 *
 * Retired as a visual switch in the 2026-10 facelift: the passport is now a
 * DOCUMENT — paper page under an ink-green cover band — on every host surface
 * and at every tier, so a tier-3 passport no longer flips the whole card
 * dark. The prop is kept so existing callers compile; both values render the
 * same card. Tier is carried by the cover band's pill, verification by the
 * ledger's stamps.
 */
export type PassportCardSurface = "auto" | "light"

export interface PassportCardProps {
  data: PassportCardData
  mode?: PassportCardMode
  surface?: PassportCardSurface
  className?: string
}

const GOAL_LABELS: Record<string, string> = {
  first_time: "First home",
  first_home: "First home",
  move_up: "Moving up",
  repeat: "Repeat buyer",
  investor: "Investment",
  downsizing: "Downsizing",
}

const TIMELINE_LABELS: Record<string, string> = {
  "0_3_months": "0–3 months",
  "3_6_months": "3–6 months",
  "6_12_months": "6–12 months",
  "6-12 months": "6–12 months",
  "6–12 months": "6–12 months",
  "12_plus": "12+ months",
}

const ACTIVITY_LABELS: Record<string, string> = {
  ready: "Ready to act",
  active: "Active",
  exploring: "Exploring",
}

const LINE_LABELS: Record<string, string> = {
  income: "Income",
  household_income: "Income",
  down_payment: "Down payment",
  assets: "Total assets",
  debts: "Debts",
  monthly_debts: "Debts",
  credit: "Credit",
  credit_score: "Credit",
}

const PROVENANCE_LABELS: Record<string, string> = {
  verified: "Verified",
  connected: "Verified",
  pre_approval: "Pre-approved",
  document: "Document",
  doc: "Document",
  self_reported: "Self-reported",
  self: "Self-reported",
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ")
}

function titleizeToken(raw: string): string {
  return raw
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function labelFromMap(raw: string | null | undefined, map: Record<string, string>): string | null {
  if (!raw) return null
  return map[raw] ?? titleizeToken(raw)
}

function formatMoneyCompact(value: number): string {
  if (!Number.isFinite(value)) return "$0"
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  if (Math.abs(value) >= 1_000) {
    return `$${Math.round(value / 1_000)}k`
  }
  return `$${Math.round(value).toLocaleString()}`
}

export function formatPassportBand(band: PassportMoneyBand | null | undefined): string | null {
  if (!band) return null
  const low = Number(band.low)
  const high = Number(band.high)
  if (!Number.isFinite(low) || !Number.isFinite(high)) return null

  if (band.period === "score") {
    return `${Math.round(low)}–${Math.round(high)}`
  }

  // Equal bounds are the consumer bucketing's "at or below the band edge"
  // shape: the backend clamps a tiny value's floor up to one band width while
  // the ceiling rounds to the same edge, so a sub-$10k income arrives as
  // {low: 10_000, high: 10_000}. Render honest "Under $X" copy, never the
  // degenerate "$10k–$10k/yr".
  const value =
    low === high
      ? `Under ${formatMoneyCompact(high)}`
      : `${formatMoneyCompact(low)}–${formatMoneyCompact(high)}`
  if (band.period === "annual") return `${value}/yr`
  if (band.period === "monthly") return `${value}/mo`
  return value
}

/**
 * Renders estimated buying power as a band when a genuine low exists, and as a
 * single "up to $X" figure when it does not (the post-SILL-30 shape — see
 * `PassportBuyingPower.low`). Returns null rather than inventing a floor, and
 * never emits a `$0` edge for a missing or non-positive bound.
 */
export function formatBuyingPower(power: PassportBuyingPower | null | undefined): string | null {
  if (!power) return null
  const high = Number(power.high)
  if (!Number.isFinite(high) || high <= 0) return null

  const low = power.low == null ? null : Number(power.low)
  if (low != null && Number.isFinite(low) && low > 0) {
    return `${formatMoneyCompact(low)}–${formatMoneyCompact(high)}`
  }
  return `up to ${formatMoneyCompact(high)}`
}

function areaLabel(raw: string | null | undefined): string | null {
  return raw?.trim() || null
}

function monthDayLabel(iso: string | undefined): string | null {
  if (!iso) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  const month = Number(match[2])
  const day = Number(match[3])
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return `${names[month - 1]} ${day}`
}

function rateStamp(power: PassportBuyingPower | null | undefined): string | null {
  if (!power?.rate_percent) return null
  const rate = Number.parseFloat(power.rate_percent.toFixed(2)).toString()
  const date = monthDayLabel(power.rate_as_of)
  return date ? `est. at ${rate}% · as of ${date}` : `est. at ${rate}%`
}

function displayCode(data: PassportCardData): string {
  if (data.display_code) return data.display_code
  // Defense in depth: a surface that forgets to supply a pseudonymous handle
  // must NOT print the raw profile id (ADR-038). Shorten to the same 4-char
  // shape the marketplace's own DisplayCodeFromHandle produces.
  const clean = data.passport_id.replace(/-/g, "").toUpperCase()
  return `Buyer #${clean.slice(0, 4)}`
}

function tierLabel(tier: string | undefined): string | null {
  if (!tier) return null
  const normalized = tier.toUpperCase()
  return normalized.startsWith("T") ? normalized : titleizeToken(tier)
}

function provenanceLabel(raw: string | null | undefined): string | null {
  if (!raw) return null
  return PROVENANCE_LABELS[raw] ?? titleizeToken(raw)
}

export type PassportBadgeTone = "bank" | "doc" | "self"

export interface PassportBadgeProps {
  tone: PassportBadgeTone
  label: string
  /** Render on the dark (tier-3) card surface. */
  dark?: boolean
}

export function provenanceTone(raw: string | null | undefined): PassportBadgeTone {
  if (raw === "verified" || raw === "connected" || raw === "pre_approval") return "bank"
  if (raw === "document" || raw === "doc") return "doc"
  return "self"
}

function darkStampStyle(tone: PassportBadgeTone): CSSProperties {
  if (tone === "doc") {
    return { borderColor: "rgba(125, 196, 240, 0.8)", color: "#bfe3ff" }
  }
  if (tone === "self") {
    return { borderColor: "rgba(255, 255, 255, 0.4)", color: "rgba(255, 255, 255, 0.7)" }
  }
  return { borderColor: "#9fe3c5", color: "#9fe3c5" }
}

// Explicit inline dimensions (not Tailwind size-*) so the icon renders at the
// right size in every consuming app regardless of that app's utility emission
// — same pattern as MapPinIcon/ShieldIcon above.
function CheckIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.75"
      style={{ flexShrink: 0, height: 10, width: 10 }}
      viewBox="0 0 24 24"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/**
 * PassportBadge — the ONE provenance mark for every passport surface
 * (consumer Your view, consumer Agent view, pro marketplace). Rendered as an
 * outlined ink STAMP rather than a filled pill (2026-10 facelift): solid
 * green for bank-verified (with the check), solid blue for document-backed,
 * dashed gray for self-reported. The stamp is the passport's own vocabulary —
 * the funnel's form chips are filled pills, so the two can't be confused.
 */
export function PassportBadge({ tone, label, dark = false }: PassportBadgeProps): ReactElement {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] border-[1.5px] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em]",
        tone === "self" && "border-dashed font-semibold",
        !dark && tone === "bank" && "border-primary text-primary",
        !dark && tone === "doc" && "border-info text-foreground",
        !dark && tone === "self" && "border-muted-foreground/60 text-muted-foreground",
      )}
      style={dark ? darkStampStyle(tone) : undefined}
    >
      {tone === "bank" && <CheckIcon />}
      {label}
    </span>
  )
}

function MapPinIcon({ className, style }: { className?: string; style?: CSSProperties }): ReactElement {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.9"
      style={style}
      viewBox="0 0 24 24"
    >
      <path d="M20 10c0 6-8 11-8 11s-8-5-8-11a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  )
}

function ShieldIcon({ style }: { style?: CSSProperties }): ReactElement {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
      style={style}
      viewBox="0 0 24 24"
    >
      <path d="M12 3 19 6v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" />
    </svg>
  )
}

function tierNumber(tier: string | undefined): string | null {
  const label = tierLabel(tier)
  const match = label?.match(/\d+/)
  return match?.[0] ?? null
}

export type TierPillSize = "default" | "sm"

export interface TierPillProps {
  /** Raw tier enum (`t1`/`t2`/`t3`), never a pre-formatted "T2" string. */
  tier: string | undefined
  /** Render on the dark (tier-3) card surface. */
  dark?: boolean
  /** Dense variant for tight contexts (table rows). Defaults to the full
   * passport-card size. */
  size?: TierPillSize
}

/**
 * TierPill — the ONE "TIER N" pill for every marketplace surface that shows
 * a buyer's verification tier (passport card, requests inbox, request
 * detail, connections ledger). Renders nothing for an unrecognized/absent
 * tier rather than falling back to a raw enum token.
 */
export function TierPill({ tier, dark = false, size = "default" }: TierPillProps): ReactElement | null {
  const num = tierNumber(tier)
  if (!num) return null
  const sm = size === "sm"
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full font-semibold uppercase tracking-[0.12em]",
        sm ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-0.5 text-[10px]",
        !dark && "bg-primary/10 text-primary",
      )}
      style={dark ? { backgroundColor: "rgba(255, 255, 255, 0.12)", color: "#7fd3b2" } : undefined}
    >
      <ShieldIcon style={{ flexShrink: 0, height: sm ? 10 : 12, width: sm ? 10 : 12 }} />
      TIER {num}
    </span>
  )
}

interface DisplayLine {
  key: string
  label: string
  value: string
  provenanceRaw?: string | null
  provenance?: string | null
  subtext?: string | null
}

function buildDisplayLines(lines: PassportLine[] | undefined, mode: PassportCardMode): DisplayLine[] {
  const provided = (lines ?? []).filter((line) => {
    if (!line.band) return false
    if (mode === "agent") return line.state === "provided"
    return line.state !== "not_provided"
  })
  const byKind = new Map(provided.map((line) => [line.kind, line]))
  const row = (kind: string, label = LINE_LABELS[kind]): DisplayLine | null => {
    const line = byKind.get(kind)
    const value = formatPassportBand(line?.band)
    if (!line || !value || !label) return null
    return {
      key: kind,
      label,
      value,
      provenanceRaw: line.provenance,
      provenance: provenanceLabel(line.provenance),
    }
  }

  const out: DisplayLine[] = []
  const income = row("income") ?? row("household_income")
  if (income) out.push(income)

  const assets = byKind.get("assets")
  const assetsValue = formatPassportBand(assets?.band)
  const downPayment = row("down_payment")
  if (downPayment) {
    downPayment.subtext =
      assets && assetsValue
        ? `Backed by ${provenanceLabel(assets.provenance)?.toLowerCase() ?? "reported"} assets · leaves ${assetsValue} in reserves`
        : null
    out.push(downPayment)
  } else {
    const assetsRow = row("assets")
    if (assetsRow) out.push(assetsRow)
  }

  const debts = row("debts") ?? row("monthly_debts")
  if (debts) out.push(debts)

  const credit = row("credit") ?? row("credit_score")
  if (credit) out.push(credit)

  const used = new Set(out.map((item) => item.key))
  for (const line of provided) {
    if (used.has(line.kind) || line.kind === "assets") continue
    const value = formatPassportBand(line.band)
    if (!value || !LINE_LABELS[line.kind]) continue
    out.push({
      key: line.kind,
      label: LINE_LABELS[line.kind],
      value,
      provenanceRaw: line.provenance,
      provenance: provenanceLabel(line.provenance),
    })
  }
  return out
}

/* Cover-band texture: faint diagonal guilloché lines, the one decorative
 * element on the card. Inline because it is a gradient image, not a token. */
const COVER_TEXTURE: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(135deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 9px)",
}

const COVER_LABEL: CSSProperties = {
  color: "rgba(255, 255, 255, 0.55)",
  fontSize: "10.5px",
  fontWeight: 500,
  letterSpacing: "0.14em",
  lineHeight: 1.2,
  textTransform: "uppercase",
}

/* Data-page field label — the passport's own tracked caps, deliberately NOT
 * the app's `eyebrow-label` (that is a section header; this is a field name
 * on a document). */
const FIELD_LABEL: CSSProperties = {
  fontSize: "10px",
  fontWeight: 600,
  letterSpacing: "0.14em",
  lineHeight: 1.2,
  textTransform: "uppercase",
}

/**
 * PassportCard — the Buyer Passport as a DOCUMENT (2026-10 facelift): an
 * ink-green cover band (Sill mark, tier pill, the pseudonymous handle set in
 * mono like a document number) over a paper data page (place name in the
 * display face, a two-column fact grid, a ledger of financial lines with
 * provenance stamps). The same card renders on the funnel's go-live screen,
 * the consumer dashboard and the pro marketplace, so its anatomy is pinned
 * here and never re-drawn per surface.
 */
export function PassportCard({
  data,
  mode = "agent",
  className,
}: PassportCardProps): ReactElement {
  const code = displayCode(data)
  const goal = labelFromMap(data.goal, GOAL_LABELS)
  const area = areaLabel(data.target_area)
  const headline = area
  const price = data.price_kind === "shopping_range" && data.price_bracket && data.price_bracket.low === data.price_bracket.high
    ? formatMoneyCompact(data.price_bracket.low)
    : formatPassportBand(data.price_bracket)
  const timeline = labelFromMap(data.timeline, TIMELINE_LABELS)
  const buyingPower = formatBuyingPower(data.buying_power)
  const rate = rateStamp(data.buying_power)
  const activity = labelFromMap(data.activity_bucket, ACTIVITY_LABELS)
  const lines = buildDisplayLines(data.lines, mode)
  // Show ONE affordability figure, never two (operator 2026-08-13). The
  // OUTWARD passport is the price bracket — that is what an agent can act on,
  // and a bracket beside a buying-power figure invites the two to disagree in
  // front of the person being represented. Buying power stays on the buyer's
  // OWN view, where it is their planning number rather than a claim to a pro.
  const showBuyingPower = Boolean(buyingPower) && mode === "own"
  // Green is THIS card's verification color — `PassportBadge` stamps a
  // bank-verified line green and a self-reported one gray. So the card's own
  // green accents (the pin, the buying-power inset) must not appear on a
  // passport with nothing bank-verified, or the chrome contradicts the gray
  // stamps it frames (operator 2026-08-13: "the passport should only be green
  // if verified"). The TIER PILL is deliberately exempt (operator: "the pill
  // is fine") — it reads as an identity badge for the tier itself, not as a
  // verification claim about the facts below it. Derived from the lines
  // actually RENDERED, so the decoration can never out-claim the card; `t3`
  // is the equivalent tier rollup.
  const verified =
    data.tier === "t3" || lines.some((line) => provenanceTone(line.provenanceRaw) === "bank")
  const metrics = [
    goal ? { label: "Home status", value: goal } : null,
    timeline ? { label: "Timeline", value: timeline } : null,
    price ? { label: priceLabel(data.price_kind, data.price_approximate), value: price } : null,
    activity ? { label: "Activity", value: activity } : null,
  ].filter((metric): metric is { label: string; value: string } => Boolean(metric))

  return (
    <section
      className={cx(
        "overflow-hidden rounded-[20px] border border-border bg-card text-left text-foreground",
        className,
      )}
      style={{
        boxShadow:
          "0 1px 2px rgba(23, 41, 39, 0.06), 0 20px 40px -24px rgba(23, 41, 39, 0.35)",
      }}
      data-testid="passport-card"
    >
      <div
        className="relative flex items-center justify-between gap-4 px-6 py-[18px] text-white"
        data-testid="passport-cover"
        style={{ backgroundColor: "var(--pro-deep, #10261d)" }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={COVER_TEXTURE} />
        <div className="relative flex items-center gap-3">
          <SillLogo
            barColor="#7fd3b2"
            color="#ffffff"
            style={{ display: "block", flexShrink: 0, height: 28, width: 30 }}
          />
          <span className="block" style={COVER_LABEL}>
            Buyer passport
          </span>
        </div>
        <div className="relative flex shrink-0 flex-col items-end gap-1.5">
          <TierPill tier={data.tier} dark />
          <span
            className="font-mono"
            style={{ color: "rgba(255, 255, 255, 0.72)", fontSize: 12, letterSpacing: "0.1em" }}
          >
            {code}
          </span>
        </div>
      </div>

      <div className="px-6 pb-5 pt-[22px]">
        {headline && (
          <div className="flex min-w-0 items-center gap-2.5">
            <MapPinIcon
              style={{
                // Neutral unless the passport is actually bank-verified —
                // see `verified` above.
                color: verified ? "var(--primary, #0f6e56)" : "var(--muted-foreground, #6b7280)",
                flexShrink: 0,
                height: 18,
                width: 18,
              }}
            />
            <p
              className="font-display text-foreground"
              style={{ fontSize: 23, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2 }}
            >
              {headline}
            </p>
          </div>
        )}

        {showBuyingPower && (
          <div
            className="mt-[18px] rounded-xl border px-4 py-3.5"
            style={
              verified
                ? { backgroundColor: "#f4f8f6", borderColor: "#e6f0eb" }
                : // Document-backed or self-reported: the buying-power inset
                  // drops its green wash for a neutral one.
                  { backgroundColor: "var(--muted, #f2f1ef)", borderColor: "var(--border, #e3e1dd)" }
            }
          >
            <p
              className={verified ? "text-primary-dark" : "text-muted-foreground"}
              style={FIELD_LABEL}
            >
              Estimated buying power
            </p>
            <p
              className="mt-1 font-display text-foreground"
              style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.1, whiteSpace: "nowrap" }}
            >
              {buyingPower}
            </p>
            {rate && (
              <p className="mt-1 text-muted-foreground" style={{ fontSize: "12.5px", lineHeight: 1.35 }}>
                {rate}
              </p>
            )}
          </div>
        )}

        {metrics.length > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3.5 border-t border-border pt-[18px]">
            {metrics.map((metric) => (
              <div key={metric.label}>
                <p className="text-muted-foreground" style={FIELD_LABEL}>
                  {metric.label}
                </p>
                <p className="mt-0.5 text-foreground tabular-nums" style={{ fontSize: "15.5px", fontWeight: 600 }}>
                  {metric.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {lines.length > 0 && (
          <div className="mt-[18px] border-t border-border" data-testid="passport-ledger">
            {lines.map((line) => (
              <div
                key={line.key}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-3.5 border-b border-border py-3"
              >
                <div className="min-w-0">
                  <p className="text-muted-foreground" style={FIELD_LABEL}>
                    {line.label}
                  </p>
                  {line.subtext && (
                    <p className="mt-0.5 text-muted-foreground" style={{ fontSize: 12, lineHeight: 1.4 }}>
                      {line.subtext}
                    </p>
                  )}
                </div>
                <p
                  className="whitespace-nowrap text-foreground tabular-nums"
                  style={{ fontSize: 15, fontWeight: 600 }}
                >
                  {line.value}
                </p>
                {line.provenance ? (
                  <PassportBadge label={line.provenance} tone={provenanceTone(line.provenanceRaw)} />
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        )}

        <p className="mt-3.5 text-muted-foreground" style={{ fontSize: "12.5px" }}>
          Ranges only, never exact figures.
        </p>
      </div>
    </section>
  )
}
